import { ref, computed } from 'vue';
import { fetchOverrides, fetchCustomSchemaDefinition } from '@/composables/custom-data/customData';

interface Override {
  id_custom_data_definition: number;
  custom_field_name: string;
  system_field_name: string;
  target_system_schema: string;
  priority: number;
  schemaName?: string;
}

interface OverridesCache {
  overrides: Override[];
  loaded: boolean;
  loading: boolean;
  error: boolean;
}

const cache = ref<OverridesCache>({
  overrides: [],
  loaded: false,
  loading: false,
  error: false,
});

export default function useProductOverrides() {
  /**
   * Load all overrides for products schema
   */
  const loadOverrides = async () => {
    if (cache.value.loaded || cache.value.loading) {
      return;
    }

    cache.value.loading = true;
    cache.value.error = false;

    try {
      // Fetch all overrides for products (API doesn't filter by system_field_name correctly)
      const allProductOverrides = await fetchOverrides('products', 'price');

      // Filter manually by system_field_name to get unique overrides
      const uniqueOverrides = allProductOverrides.filter(
        (override: any, index: number, self: any[]) => index === self.findIndex(
          (o: any) => o.id_custom_data_definition === override.id_custom_data_definition
            && o.custom_field_name === override.custom_field_name
            && o.system_field_name === override.system_field_name
            && o.priority === override.priority,
        ),
      );

      // Load schema names for each override
      const overridesWithSchemaNames = await Promise.all(
        uniqueOverrides.map(async (override: any): Promise<Override> => {
          try {
            const schemaResponse = await fetchCustomSchemaDefinition(override.id_custom_data_definition);

            const schemaName = schemaResponse.success && schemaResponse.data && schemaResponse.data.name
              ? schemaResponse.data.name
              : null;

            return {
              id_custom_data_definition: override.id_custom_data_definition,
              custom_field_name: override.custom_field_name,
              system_field_name: override.system_field_name,
              target_system_schema: override.target_system_schema,
              priority: override.priority,
              schemaName: schemaName || `Schema #${override.id_custom_data_definition}`,
            };
          } catch (error) {
            console.error('Error loading schema name:', error);
            return {
              id_custom_data_definition: override.id_custom_data_definition,
              custom_field_name: override.custom_field_name,
              system_field_name: override.system_field_name,
              target_system_schema: override.target_system_schema,
              priority: override.priority,
              schemaName: `Schema #${override.id_custom_data_definition}`,
            };
          }
        }),
      );

      cache.value.overrides = overridesWithSchemaNames;
      cache.value.loaded = true;
    } catch (error) {
      console.error('Error loading overrides:', error);
      cache.value.error = true;
    } finally {
      cache.value.loading = false;
    }
  };

  /**
   * Get overrides for a specific system field, sorted by priority
   */
  const getOverridesForField = (systemFieldName: string): Override[] => cache.value.overrides
    .filter((override) => override.system_field_name === systemFieldName)
    .sort((a, b) => a.priority - b.priority);

  /**
   * Check if a specific system field has overrides
   * @param systemFieldName - The system field name (e.g., 'price')
   * @param parentContext - Optional parent context ID (e.g., 'product_visited_attributes')
   */
  const hasOverrides = (systemFieldName: string, parentContext?: string): boolean => {
    // Check if systemFieldName is valid
    if (!systemFieldName || !['price', 'price_discount'].includes(systemFieldName)) {
      return false;
    }

    // Check if parentContext is provided and valid
    if (parentContext && !['product_visited_attributes'].includes(parentContext)) {
      return false;
    }

    if (!cache.value.loaded || cache.value.error) {
      return false;
    }

    // If no context provided, check all overrides (backward compatibility)
    if (!parentContext) {
      return cache.value.overrides.some((override) => override.system_field_name === systemFieldName);
    }

    // show overrides for 'products' schema
    return cache.value.overrides.some(
      (override) => override.system_field_name === systemFieldName
        && override.target_system_schema === 'products',
    );
  };

  /**
   * Check if overrides are currently loading
   */
  const isLoading = computed(() => cache.value.loading);

  /**
   * Check if there was an error loading overrides
   */
  const hasError = computed(() => cache.value.error);

  /**
   * Check if overrides are loaded
   */
  const isLoaded = computed(() => cache.value.loaded);

  return {
    loadOverrides,
    getOverridesForField,
    hasOverrides,
    isLoading,
    hasError,
    isLoaded,
  };
}
